import {
    Box,
    Grid,
    Popover,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import EastIcon from '@mui/icons-material/East';
import moment from 'moment';
import React, { forwardRef, useRef, useState } from 'react';
import { DateRange } from 'react-date-range';
import { useDispatch, useSelector } from 'react-redux';
import {
    resetDates,
    selectEndDate,
    selectStartDate,
    setEndDate,
    setStartDate,
} from '../../app/Slices/DateFilterSlice';
import { CustomButton } from '../../layout/styledcomponents/CustomButton';
import { CustomTextField } from '../../layout/styledcomponents/CustomTextField';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file

const DateFilter = () => {
    const startDate = useSelector(selectStartDate);
    const endDate = useSelector(selectEndDate);
    const isMobile = useMediaQuery('(max-width: 900px)');
    const firstTextFieldRef = useRef(null);
    const secondTextFieldRef = useRef(null);

    const [openCalendar, setOpenCalendar] = useState(false);
    const [anchorElCalendar, setAnchorElCalendar] =
        React.useState<null | HTMLElement>(null);
    const dispatch = useDispatch();

    const handleCalendar = (event: React.MouseEvent<HTMLElement>) => {
        setOpenCalendar((open) => !open);
        setAnchorElCalendar(event.currentTarget);
    };

    const handleCalendarClose = () => {
        setOpenCalendar(false);
        setAnchorElCalendar(null);
    };

    const handleDateChange = (date: any) => {
        dispatch(setStartDate(date.range1.startDate));

        dispatch(setEndDate(date.range1.endDate));
    };

    const theme = useTheme();

    return (
        <Grid sx={{ display: 'flex', width: isMobile ? '100%' : 'auto' }}>
            {isMobile ? (
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'flex-end',
                        gap: 1,
                    }}
                >
                    <IndividualDate
                        label='Check in'
                        date={startDate}
                        handleClick={handleCalendar}
                        ref={firstTextFieldRef}
                    />
                    <Box
                        sx={{
                            flexShrink: 0,
                            alignSelf: 'flex-end',
                            pb: 1,
                            color: 'text.secondary',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <EastIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <IndividualDate
                        label='Check out'
                        date={endDate}
                        handleClick={handleCalendar}
                        ref={secondTextFieldRef}
                    />
                </Box>
            ) : (
                <>
                    <Grid
                        sx={{
                            borderLeft: `1px solid ${theme.palette.faded.main}`,
                            height: 'auto',
                            paddingRight: '20px',
                        }}
                    />
                    <IndividualDate
                        label='Check in'
                        date={startDate}
                        handleClick={handleCalendar}
                        ref={firstTextFieldRef}
                    />
                    <Grid
                        sx={{
                            borderLeft: `1px solid ${theme.palette.faded.main}`,
                            height: 'auto',
                            paddingRight: '20px',
                        }}
                    />
                    <IndividualDate
                        label='Check out'
                        date={endDate}
                        handleClick={handleCalendar}
                        ref={secondTextFieldRef}
                    />
                    <Grid
                        sx={{
                            borderLeft: `1px solid ${theme.palette.faded.main}`,
                            height: 'auto',
                        }}
                    />
                </>
            )}

            <Popover
                open={openCalendar}
                anchorEl={anchorElCalendar}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                onClose={handleCalendarClose}
                sx={{ display: 'flex' }}
            >
                <DateRange
                    editableDateInputs={true}
                    onChange={handleDateChange}
                    moveRangeOnFirstSelection={false}
                    ranges={[
                        {
                            startDate: startDate ? startDate : new Date(),
                            endDate: endDate ? endDate : new Date(),
                        },
                    ]}
                    rangeColors={[theme.palette.info.main]}
                    months={isMobile ? 1 : 2}
                    direction='horizontal'
                    showMonthAndYearPickers={false}
                    minDate={new Date()}
                    color='red'
                    showPreview={true}
                    dragSelectionEnabled={true}
                    showDateDisplay={false}
                    weekStartsOn={1}
                />
                <Grid
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        cursor: 'pointer',
                    }}
                >
                    <CustomButton
                        onClick={() => {
                            dispatch(resetDates());
                        }}
                        sx={{
                            color: `${theme.palette.custom.button.mid.textColor}`,
                            fontSize: '12px',
                            '&:hover': {
                                cursor: 'pointer',
                                backgroundColor: `${theme.palette.custom.button.mid.hoverBackgroundColor}`,
                            },
                        }}
                    >
                        Clear Dates
                    </CustomButton>
                </Grid>
            </Popover>
        </Grid>
    );
};

interface IndividualDateProps {
    label: string;
    date?: Date | undefined;
    handleClick: any;
}

const IndividualDate = forwardRef<any, IndividualDateProps>(
    ({ label, date, handleClick }, ref) => {
        const isMobile = useMediaQuery('(max-width: 900px)');
        const theme = useTheme();

        const displayValue = date ? moment(date).format('DD/MM/YYYY') : '';

        return (
            <Box
                sx={{
                    width: isMobile ? undefined : '160px',
                    flex: isMobile ? '1 1 0%' : undefined,
                    minWidth: 0,
                }}
            >
                <Typography
                    variant='body2'
                    sx={{
                        fontWeight: 'medium',
                        color: theme.typography.body1.color,
                        mb: 0.35,
                    }}
                >
                    {label}
                </Typography>
                <CustomTextField
                    hiddenLabel
                    color='dark'
                    variant='standard'
                    fullWidth
                    placeholder='Add dates'
                    value={displayValue}
                    onClick={(event: any) => handleClick(event)}
                    ref={ref}
                    inputProps={{
                        autoComplete: 'none',
                        'aria-label': label,
                    }}
                    sx={{
                        border: 0,
                        '& .MuiInputBase-input': {
                            color: theme.typography.caption.color,
                            '::placeholder': {
                                color: theme.typography.caption.color,
                                fontWeight: '400',
                            },
                        },
                    }}
                    InputProps={{
                        disableUnderline: true,
                        readOnly: true,
                    }}
                />
            </Box>
        );
    },
);

export default DateFilter;
